import { COLLECTIONS, MUTATE_UPDATE_SNAPSHOT_FROM_COMMITTED } from "constants/action-types";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import { Observable } from "rxjs";
import { fromJS, Map } from "immutable";
import get from "lodash/get";
import lodashSet from "lodash/set";
import { IAllocatePayload, SyncBulkWithAllocationAction } from "../../actions/collections/collection-allocations";
import {
    mutateCollectionItem,
    lookupAssortmentErrors,
    IMutateAllocationBagValue,
    bulkMutateAllocationBag,
    clearAllocationBag,
    bulkClearAllocationBag,
} from "../../actions/collections";
import {
    AllocationBag,
    AllocationBagType,
    AllocationDimensionIndex,
    AllocationDimensionKind,
    IAllocationDimension,
    IAllocationDimensionValue,
    BAG_VALUE_PARAMS_SEPARATOR,
    getAllocationBagValuePath,
    getBagValuesByRowId,
    BagValues,
    flatToBagValues,
    isEmptyBagValues,
    syncBulksWithAllocations,
    generateReconciliationBagMutations,
    generateReconciliationMutations,
} from "helpers/allocation-dimension";
import { flatToTable, IBagTable } from "helpers/table-bag";
import {
    Item,
    ITEMS_ID,
    SizeCurveMethod,
    Kinds,
    SizingErrors,
    SizeCurveStatus,
    SizeAllocationType,
    ItemFields,
    ISizePacksTolerance,
    CollectionKeys,
    ValueTypes,
    ALLOCATION_BAG_ID,
    ALLOCATION_BAG_NAMESPACE,
    MAX_MUTATION_OPERATION_LIMIT,
} from "constants/product-collection";
import {
    sizeSelectedGetter,
    sizeCurveStatusValueFormatter,
    sizeCurveMethodValueFormatter,
    parseSelectedSizes,
    ISchemaKey,
    getSelectedSizePacksForItem,
    itemHasPackSizesMismatch,
    buildFieldToValueTypeMap,
} from "helpers/product-collection";

import { getFieldValue } from "helpers/collection";
import { add as snackbarAdd } from "actions/snackbars";
import { formatMessage } from "lib/globalize-util";
import {
    findOneFieldByKind,
    findSizeCurveFromLookup,
    legacySplitRowGroupBySizeCurveAndDimension,
    splitRowGroupBySizeCurveAndDimension,
    getAllocationsByRow,
    getSizePacksAllocationsByRow,
    getInfeasibleRows,
    lookupSizeCurve,
    allocateInClient,
    lookupSizeAllocation,
    IAllocationResponseData,
    SIZE_PACK_UNLIMITED_VALUE,
} from "./helpers/allocation";
import { Epic } from "redux-observable";
import {
    getDoors,
    getAllocationDimensionValues,
    getAllocationDimensionsBySubtype,
    getCollectionSizePacksByBrandId,
    getCollectionSizeAllocationType,
    getCollectionAllocationBag,
    getCollectionItems,
} from "reducers/selectors/collection";
import {
    doesBulksAndAllocationsMatchLegacy,
    doesBulksAndAllocationsMatch,
    getSizingErrorStatus,
} from "helpers/allocation";
import { IDoor } from "helpers/door-collection";
import { IPackInfo } from "reducers/collection";
import { safeParseInt } from "lib/number";
import { WindowAnalytics } from "helpers/window-analytics";
import { SizeActions } from "constants/analytics";
import { bulkMixedSetOperation, MutableType, OperationType } from "actions/mutate";
import { IStore } from "reducers";
import { getUpdatedSizesCurveAndStatus } from "components/product-collection/product-collection-item-allocation";
import { amphibiousGetIn } from "helpers/objUtils";
import { getLegacyBulkTotalsSum } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import {
    sumColumnValues,
    sumRowTotalRetailValues,
    sumRowUnitsValues,
} from "helpers/metrics/sibling-value-getter/getter";
import { generateAllocationBagMutations } from "./helpers/sizing";

interface IAnalyticsProperties {
    rowCount: number;
    numRowsAsPacks?: number;
    sizePacksTolerance?: ISizePacksTolerance;
}

const parseAllocationsBag = (allocationsStr) => {
    let allocations;

    if (!allocationsStr) {
        // no allocations, abort
        return;
    }

    try {
        allocations = JSON.parse(allocationsStr);
    } catch (err) {
        return;
    }

    return allocations;
};

interface IStartAllocateRow {
    id: string;
    sizes: string[];
    curveSizes: string[];
    rowNode: any;
    row: any;
    rowIndex: number;
    sizeCurve: string;
    selectedSizeRange: string;
    initialSizeStatus: string;
    allocationDimensions: IAllocationDimension[];
    selectedSizes: string[];
    sizePacks: {
        selectedSizePacks: IPackInfo[];
        maxPerDoor: number;
        maxPerProduct: number;
        sizePacksTolerance: ISizePacksTolerance | null;
    };
    constraintAllocation?: IBagTable | BagValues;
}

export const allocateEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.ALLOCATIONS.ALLOCATE.START)
        .map((action) => action.payload)
        .switchMap((payload) => {
            const {
                collectionId,
                itemIds,
                rawItemDisplayProperty,
                merchantId,
                allocationMethod,
                constraintAllocations,
                force,
                ignoreInfeasible,
            } = payload as IAllocatePayload;

            const state = store.getState();
            const { mutate } = state;

            const allocationDimensions = getAllocationDimensionsBySubtype(
                state,
                collectionId,
                AllocationDimensionKind.Generic
            );
            const dates = getAllocationDimensionValues(state, collectionId, AllocationDimensionIndex.Date) || [];
            const doors = getDoors(state, collectionId);

            const collectionObj = mutate.snapshots.get(collectionId);
            const itemsObjectId = collectionObj ? collectionObj.getIn(["items", "ref", "object_id"]) : undefined;
            const collectionItems = mutate.snapshots.get(itemsObjectId);
            const currentBagValues: AllocationBag = mutate.snapshots.get(ALLOCATION_BAG_ID);

            const sizeSelectedSchema = findOneFieldByKind(Kinds.SIZES_SELECTED, rawItemDisplayProperty);
            const sizeCurveSchema = findOneFieldByKind(Kinds.SIZE_PROFILE_SELECTED, rawItemDisplayProperty);
            const sizeDistributionSchema = findOneFieldByKind(Kinds.SIZE_DISTRIBUTION, rawItemDisplayProperty);
            const sizeStatusSchema = findOneFieldByKind(Kinds.SIZE_CURVE_STATUS, rawItemDisplayProperty);
            const sizeMethodSchema = findOneFieldByKind(Kinds.SIZE_CURVE_METHOD, rawItemDisplayProperty);
            const selectedSizePacksSchema = findOneFieldByKind(Kinds.PACKS_SELECTED, rawItemDisplayProperty);
            const productMaxPackSchema = findOneFieldByKind(Kinds.PACKS_MAX_PER_PROD, rawItemDisplayProperty);
            const storeMaxPackSchema = findOneFieldByKind(Kinds.PACKS_MAX_PER_DOOR, rawItemDisplayProperty);

            const columnLength = get(sizeCurveSchema, "lookup_plan.select.terms", []).length;
            const orderByField = columnLength && sizeCurveSchema.lookup_plan.select.terms[columnLength > 1 ? 1 : 0];
            const sizeProfileNameField = get(orderByField, "column.table_column", "");
            const sizeAllocationType = allocationMethod || getCollectionSizeAllocationType(state, collectionId);
            const sizePacksTolerance = collectionObj?.getIn(CollectionKeys.PACK_SIZING_TOLERANCE)?.toJS() ?? null;

            let analyticsRowsToBeSizedWithPacks = 0;
            const reducedItems = itemIds.reduce((acc, id: string, loopIndex) => {
                if (
                    !sizeSelectedSchema ||
                    !sizeCurveSchema ||
                    !sizeStatusSchema ||
                    !sizeMethodSchema ||
                    !sizeDistributionSchema
                ) {
                    return acc;
                }

                const rowIndex =
                    collectionItems &&
                    collectionItems.findIndex((collectionItem) => {
                        return collectionItem.getIn(Item.FIELDS.ID) === id;
                    });
                const row = collectionItems && collectionItems.get(rowIndex);

                // Prevent breaking changes by immitating AG-Grid row data.
                // This inherits bad pattern that was in place, accessing Grid API in an epic.
                const rowNode = {
                    data: row.toJS(),
                };

                const sizeCurveFromField = getFieldValue(rowNode.data, sizeCurveSchema.field_name);
                const selectedSizes = sizeSelectedGetter(sizeSelectedSchema, rowNode);
                // If `force` and size curve is manual, make it empty to do another lookup
                const sizeCurve =
                    sizeCurveFromField === SizeCurveStatus.Manual && force ? undefined : sizeCurveFromField;
                const selectedSizeRange = getFieldValue(rowNode.data, Item.FIELDS.SELECTED_SIZE_RANGE[1]);
                const sizeStatus = sizeCurveStatusValueFormatter(sizeStatusSchema, {
                    value: getFieldValue(rowNode.data, sizeStatusSchema.field_name),
                });
                const sizeMethod = sizeCurveMethodValueFormatter(sizeMethodSchema, {
                    value: getFieldValue(rowNode.data, sizeMethodSchema.field_name),
                });

                const selectedSizePackIds = getSelectedSizePacksForItem(row, selectedSizePacksSchema);
                selectedSizePackIds?.length && analyticsRowsToBeSizedWithPacks++;

                if (
                    !selectedSizes ||
                    !sizeProfileNameField ||
                    sizeMethod === SizeCurveMethod.Error ||
                    (sizeCurve === SizeCurveMethod.Manual && !force) ||
                    (!sizeCurve && !row)
                ) {
                    return acc;
                }

                // Check to see if there are bulks to allocated...no sense in doing all that work for nothing
                let foundValidQuantity = false;
                if (!currentBagValues) {
                    for (const r of get(rowNode, ["data", "bulks", "r"], [])) {
                        const quantity = parseInt(r.c[r.c.length - 1], 10);
                        if (!isNaN(quantity) && quantity !== 0) {
                            foundValidQuantity = true;
                            break;
                        }
                    }
                } else {
                    const bulksBagValues = getBagValuesByRowId(AllocationBagType.bulks, currentBagValues, id);
                    foundValidQuantity = bulksBagValues.some((bagValue) => +bagValue.get("string_value", "0") > 0);
                }

                if (!foundValidQuantity) {
                    return acc;
                }

                const { allSizes, curveSizes } = parseSelectedSizes(
                    selectedSizes,
                    sizeSelectedSchema.field_value_delimiter
                );
                const brandSizePacks = getCollectionSizePacksByBrandId(state, row.getIn(Item.FIELDS.BRAND_ID));
                acc.push({
                    id,
                    rowNode,
                    sizeCurve,
                    row,
                    rowIndex,
                    sizes: allSizes,
                    curveSizes,
                    selectedSizeRange,
                    initialSizeStatus: sizeStatus,
                    allocationDimensions,
                    selectedSizes,
                    sizePacks: {
                        selectedSizePacks: selectedSizePackIds
                            .map((selectedSizePackId) => brandSizePacks?.[selectedSizePackId])
                            .filter((pack) => !!pack),
                        maxPerProduct:
                            productMaxPackSchema && !productMaxPackSchema.hidden
                                ? safeParseInt(
                                      row.getIn([ItemFields.Fields, productMaxPackSchema.field_name, "string_value"]),
                                      SIZE_PACK_UNLIMITED_VALUE
                                  )
                                : SIZE_PACK_UNLIMITED_VALUE,
                        maxPerDoor:
                            storeMaxPackSchema && !storeMaxPackSchema.hidden
                                ? safeParseInt(
                                      row.getIn([ItemFields.Fields, storeMaxPackSchema.field_name, "string_value"]),
                                      SIZE_PACK_UNLIMITED_VALUE
                                  )
                                : SIZE_PACK_UNLIMITED_VALUE,
                        sizePacksTolerance,
                    },
                    constraintAllocation: constraintAllocations?.[loopIndex],
                });

                return acc;
            }, [] as Array<IStartAllocateRow>);

            const analyticsProperties: IAnalyticsProperties = {
                rowCount: itemIds.length,
            };
            if (analyticsRowsToBeSizedWithPacks > 0) {
                analyticsProperties.numRowsAsPacks = analyticsRowsToBeSizedWithPacks;
                analyticsProperties.sizePacksTolerance = sizePacksTolerance;
            }
            WindowAnalytics.track(SizeActions.ApplySizeCurve, analyticsProperties);
            const fieldToValueTypeMap = buildFieldToValueTypeMap(rawItemDisplayProperty);
            // batch one ROW at a time for processing
            return Observable.from(reducedItems)
                .delay(1)
                .concatMap((startAllocateRow: IStartAllocateRow) => {
                    const sizePacksAllocateRow = startAllocateRow.sizePacks?.selectedSizePacks.length > 0;

                    if (sizePacksAllocateRow) {
                        return allocateBySizePacksObservable({
                            allocateRow: startAllocateRow,
                            sizeCurveSchema,
                            sizeStatusSchema,
                            sizeDistributionSchema,
                            sizeProfileNameField,
                            doors,
                            dates,
                            merchantId,
                            collectionId,
                            fieldToValueTypeMap,
                            allocationBag: currentBagValues,
                        });
                    } else if (sizeAllocationType === SizeAllocationType.Class) {
                        return allocateByClassObservable({
                            allocateRow: startAllocateRow,
                            sizeCurveSchema,
                            sizeStatusSchema,
                            sizeDistributionSchema,
                            sizeProfileNameField,
                            doors,
                            dates,
                            merchantId,
                            collectionId,
                            fieldToValueTypeMap,
                            allocationBag: currentBagValues,
                        });
                    }

                    return allocateByRowObservable({
                        allocateRow: startAllocateRow,
                        sizeCurveSchema,
                        sizeStatusSchema,
                        sizeDistributionSchema,
                        sizeProfileNameField,
                        doors,
                        dates,
                        merchantId,
                        collectionId,
                        fieldToValueTypeMap,
                        allocationBag: currentBagValues,
                    });
                })
                .concatMap((singleAllocation) => {
                    const sizePacksAllocateRow = singleAllocation.sizePacks?.selectedSizePacks.length > 0;

                    if (sizePacksAllocateRow && sizeAllocationType === SizeAllocationType.Row) {
                        // When sizing by packs in `SizeAllocationType.Row` mode, we want to size it row by row instead of all at once (SizeAllocationType.Class).
                        return processAllocationsBySizePacks({
                            allocationResults: [singleAllocation],
                            doors,
                            sizeCurveSchema,
                            sizeStatusSchema,
                            selectedSizePacksSchema,
                            store,
                            ignoreInfeasible,
                            collectionId,
                            merchantId,
                            allocationBag: currentBagValues,
                        }).map((singleResult) => {
                            // Has to reformat this to this structure so it can be processed downstream.
                            return {
                                rowData: singleResult,
                            };
                        });
                    } else if (sizeAllocationType === SizeAllocationType.Row) {
                        const { row, rowIndex, allocations, rowData: fieldsToUpdate } = singleAllocation;
                        if (fieldsToUpdate) {
                            let updatedRow = Object.keys(fieldsToUpdate).reduce(
                                (mutatedRow, fieldName) =>
                                    mutatedRow.setIn(["fields", fieldName, "string_value"], fieldsToUpdate[fieldName]),
                                row
                            );
                            if (!currentBagValues) {
                                updatedRow = updatedRow.setIn(Item.ALLOCATIONS, flatToTable(allocations));
                            } else {
                                const bagValueMutations = generateAllocationBagMutations(
                                    allocations,
                                    row.getIn(Item.FIELDS.ID),
                                    currentBagValues,
                                    AllocationBagType.allocations
                                );

                                store.dispatch(bulkMutateAllocationBag(collectionId, bagValueMutations));
                            }

                            store.dispatch(
                                mutateCollectionItem({ itemsId: ITEMS_ID, index: rowIndex, item: updatedRow })
                            );
                        }
                        return Observable.from([singleAllocation]);
                    }

                    return Observable.from([singleAllocation]);
                })
                .toArray()
                .concatMap((assortmentsAllocated) => {
                    let resultsObservables = Observable.from([]);

                    if (sizeAllocationType === SizeAllocationType.Class) {
                        // `nonSizePackResults` is only used when `sizeAllocationType === SizeAllocationType.Class`
                        const { nonSizePackResults, sizePackResults } = assortmentsAllocated.reduce(
                            (acc, result) => {
                                // If row uses size packs
                                if (result.sizePacks?.selectedSizePacks.length > 0) {
                                    acc.sizePackResults.push(result);
                                } else {
                                    acc.nonSizePackResults.push(result);
                                }

                                return acc;
                            },
                            {
                                nonSizePackResults: [],
                                sizePackResults: [],
                            }
                        );

                        if (nonSizePackResults.length > 0) {
                            resultsObservables = Observable.concat(
                                resultsObservables,
                                processAllocationsByClass({
                                    allocationResults: nonSizePackResults,
                                    doors,
                                    sizeCurveSchema,
                                    sizeStatusSchema,
                                    store,
                                    ignoreInfeasible,
                                    bagValues: currentBagValues,
                                })
                            );
                        }

                        if (sizePackResults.length > 0) {
                            resultsObservables = Observable.concat(
                                resultsObservables,
                                processAllocationsBySizePacks({
                                    allocationResults: sizePackResults,
                                    doors,
                                    sizeCurveSchema,
                                    sizeStatusSchema,
                                    selectedSizePacksSchema,
                                    store,
                                    ignoreInfeasible,
                                    collectionId,
                                    merchantId,
                                })
                            );
                        }
                    } else if (sizeAllocationType === SizeAllocationType.Row) {
                        resultsObservables = Observable.concat(
                            resultsObservables,
                            processAllocationsByRow({
                                allocationResults: assortmentsAllocated,
                            })
                        );
                    }

                    return resultsObservables.toArray().concatMap((allocationResults) => {
                        return [
                            lookupAssortmentErrors(allocationResults),
                            { type: COLLECTIONS.COLLECTION.ALLOCATIONS.ALLOCATE.STOP },
                            {
                                type: COLLECTIONS.COLLECTION.ALLOCATIONS.ALLOCATE.COMPLETED,
                            },
                        ];
                    });
                })
                .catch((err) => {
                    const errorMessage =
                        err.status === 401 || err.status === 403
                            ? formatMessage("You do not have permissions to fetch size curves")
                            : formatMessage("Error allocating assortments");
                    return Observable.merge(
                        Observable.of(snackbarAdd({ message: errorMessage })),
                        Observable.of({
                            type: COLLECTIONS.COLLECTION.ALLOCATIONS.ALLOCATE.STOP,
                        })
                    );
                })
                .takeUntil(action$.ofType(COLLECTIONS.COLLECTION.ALLOCATIONS.ALLOCATE.STOP));
        });
};

interface IAllocateByClassObservable {
    allocateRow: IStartAllocateRow;
    sizeCurveSchema: ISchemaKey;
    sizeStatusSchema: ISchemaKey;
    sizeDistributionSchema: ISchemaKey;
    sizeProfileNameField: string;
    doors: IDoor[];
    dates: IAllocationDimensionValue[];
    merchantId: string;
    collectionId: string;
    fieldToValueTypeMap?: Map<string, ValueTypes>;
    allocationBag?: AllocationBag;
}
const allocateByClassObservable = (params: IAllocateByClassObservable) => {
    const {
        collectionId,
        allocateRow,
        sizeCurveSchema,
        sizeDistributionSchema,
        sizeStatusSchema,
        sizeProfileNameField,
        doors,
        dates,
        merchantId,
        fieldToValueTypeMap,
    } = params;
    const {
        id,
        sizes,
        curveSizes,
        rowNode,
        row,
        rowIndex,
        sizeCurve,
        selectedSizeRange,
        initialSizeStatus,
        allocationDimensions,
        sizePacks,
        constraintAllocation,
        selectedSizes,
    } = allocateRow;

    return Observable.fromPromise(
        findSizeCurveFromLookup({
            sizeCurveSchema,
            merchantId,
            assortmentData: rowNode.data,
            sizes,
            sizeProfileNameField,
            selectedSizeRange,
            sizeCurve,
            initialSizeStatus,
            fieldToValueTypeMap,
        })
    )
        .concatMap((sizeCurveToUpdate) => {
            return Observable.fromPromise(
                lookupSizeCurve({
                    sizeCurveSchema,
                    sizeDistributionSchema,
                    merchantId,
                    assortmentData: rowNode.data,
                    doors,
                    sizes,
                    curveSizes,
                    dates: dates.filter((date) => date.included),
                    sizeCurveToUpdate: sizeCurveToUpdate ? sizeCurveToUpdate.sizeCurve : null,
                    allocationDimensions,
                    fieldToValueTypeMap,
                })
            ).flatMap((sizeCurve) => {
                return [
                    {
                        collectionId,
                        rowIndex,
                        row,
                        sizeCurve,
                        sizeCurveName: sizeCurveToUpdate?.sizeCurve,
                        dates,
                        doors,
                        sizes,
                        allocationDimensions,
                        sizePacks,
                        constraintAllocation,
                        sizingFieldsData: sizeCurveToUpdate
                            ? {
                                  [sizeCurveSchema.field_name]: sizeCurveToUpdate.sizeCurve,
                                  [sizeStatusSchema.field_name]: sizeCurveToUpdate.sizeStatus,
                              }
                            : null,
                        selectedSizes,
                    },
                ];
            });
        })
        .catch((error) => {
            if (error.status >= 400) {
                return Observable.throw(error);
            }

            return [
                {
                    collectionId,
                    itemId: id,
                    allocations: [],
                    rowIndex,
                    row,
                    allocationDimensions,
                    sizePacks,
                    dates,
                    doors,
                    sizes,
                    constraintAllocation,
                    sizingFieldsData:
                        error.codeText === SizingErrors.NoMatch
                            ? {
                                  [sizeCurveSchema.field_name]: "",
                                  [sizeStatusSchema.field_name]: SizingErrors.CurveNotFound,
                              }
                            : null,
                },
            ];
        });
};

interface IAllocateBySizePacksObservable extends IAllocateByClassObservable {}
// This does exactly what allocate by class does. In the future, this will have different logic.
// Keeping it different function name to separate the function early on.
const allocateBySizePacksObservable = (params: IAllocateBySizePacksObservable) => allocateByClassObservable(params);

interface IAllocateByRowObservable {
    allocateRow: IStartAllocateRow;
    sizeCurveSchema: ISchemaKey;
    sizeStatusSchema: ISchemaKey;
    sizeDistributionSchema: ISchemaKey;
    sizeProfileNameField: string;
    doors: IDoor[];
    dates: IAllocationDimensionValue[];
    merchantId: string;
    collectionId: string;
    fieldToValueTypeMap?: Map<string, ValueTypes>;
    allocationBag?: AllocationBag;
}
const allocateByRowObservable = (params: IAllocateByRowObservable) => {
    const {
        collectionId,
        allocateRow,
        sizeCurveSchema,
        sizeDistributionSchema,
        sizeStatusSchema,
        sizeProfileNameField,
        doors,
        dates,
        merchantId,
        fieldToValueTypeMap,
        allocationBag,
    } = params;
    const {
        id,
        sizes,
        curveSizes,
        rowNode,
        row,
        rowIndex,
        sizeCurve,
        selectedSizeRange,
        initialSizeStatus,
        allocationDimensions,
    } = allocateRow;

    return Observable.fromPromise(
        findSizeCurveFromLookup({
            sizeCurveSchema,
            merchantId,
            assortmentData: rowNode.data,
            sizes,
            sizeProfileNameField,
            selectedSizeRange,
            sizeCurve,
            initialSizeStatus,
            fieldToValueTypeMap,
        })
    )
        .concatMap((sizeCurveToUpdate) => {
            return Observable.fromPromise(
                lookupSizeAllocation({
                    sizeCurveSchema,
                    sizeDistributionSchema,
                    merchantId,
                    assortmentData: rowNode.data,
                    doors,
                    sizes,
                    curveSizes,
                    dates: dates.filter((date) => date.included),
                    sizeCurveToUpdate: sizeCurveToUpdate ? sizeCurveToUpdate.sizeCurve : null,
                    allocationDimensions,
                    fieldToValueTypeMap,
                    allocationBag,
                })
            ).flatMap(({ error, data: itemAllocationResults }) => {
                return [
                    {
                        collectionId,
                        itemId: id,
                        allocations: itemAllocationResults,
                        rowIndex,
                        row,
                        rowData: sizeCurveToUpdate
                            ? {
                                  [sizeCurveSchema.field_name]: sizeCurveToUpdate.sizeCurve,
                                  [sizeStatusSchema.field_name]: error || sizeCurveToUpdate.sizeStatus,
                              }
                            : null,
                    },
                ];
            });
        })
        .catch((error) => {
            if (error.status >= 400) {
                return Observable.throw(error);
            }

            return [
                {
                    collectionId,
                    itemId: id,
                    allocations: [],
                    rowIndex,
                    row,
                    rowData:
                        error.codeText === SizingErrors.NoMatch
                            ? {
                                  [sizeCurveSchema.field_name]: "",
                                  [sizeStatusSchema.field_name]: SizingErrors.CurveNotFound,
                              }
                            : null,
                },
            ];
        });
};

interface IProcessAllocationsByClass {
    allocationResults: any[];
    sizeCurveSchema: ISchemaKey;
    sizeStatusSchema: ISchemaKey;
    store: any;
    doors: IDoor[];
    ignoreInfeasible?: boolean;
    bagValues?: AllocationBag;
}
const processAllocationsByClass = ({
    store,
    allocationResults,
    sizeCurveSchema,
    sizeStatusSchema,
    doors,
    ignoreInfeasible,
    bagValues,
}: IProcessAllocationsByClass) => {
    const { mutate } = store.getState();
    const collectionItems = mutate.snapshots.get(ITEMS_ID);

    const splitRowGroups = legacySplitRowGroupBySizeCurveAndDimension(allocationResults, bagValues);
    const splitRowGroupRequests = Object.values(splitRowGroups);

    if (splitRowGroupRequests.length === 0) {
        return Observable.from([]);
    }

    const allocationRequests = splitRowGroupRequests.map((splitRowGroup) => {
        // Rows without size curve are grouped under request_id: "NO_CURVE"
        if (!splitRowGroup.size_curve) {
            return new Promise((resolve) => {
                resolve(allocateInClient(splitRowGroup));
            });
        }

        const requestPayload = { ...splitRowGroup };

        // `breadcrumbs` is not actual request data
        delete requestPayload.breadcrumbs;

        // Set timeout to 1 hour
        requestPayload.time_limit_ms = 36000000;

        return ajax({
            url: `${siteConfig.goAPI}/v1/assortment/sizing`,
            method: "POST",
            body: requestPayload,
            retry: 3,
        })
            .map(({ response }) => {
                return response;
            })
            .catch((error) => {
                return Observable.throw(error);
            });
    });

    return Observable.forkJoin(allocationRequests).concatMap((allocationResponses: IAllocationResponseData[]) => {
        const assortmentsData = [];
        const allocatedRows = getAllocationsByRow(allocationResponses, splitRowGroups, doors);
        const infeasibleRows = getInfeasibleRows(allocationResponses, splitRowGroups);

        Object.keys(allocatedRows).forEach((rowId) => {
            const rowIndex =
                collectionItems &&
                collectionItems.findIndex((collectionItem) => {
                    return collectionItem.getIn(Item.FIELDS.ID) === rowId;
                });
            const row = collectionItems && collectionItems.get(rowIndex);
            // This is data passed in from earlier concatMap.
            // We shouldn't use `row` from this because it might not have the most recent version.
            const rowAllocationData = allocationResults.find((row) => {
                return row.row.getIn(Item.FIELDS.ID) === rowId;
            });

            if (row && rowAllocationData && !(ignoreInfeasible && infeasibleRows[rowId])) {
                let updatedRow = rowAllocationData.row;
                const sizingFieldsData = { ...rowAllocationData.sizingFieldsData };

                const allocation = bagValues
                    ? flatToBagValues(allocatedRows[rowId])
                    : flatToTable(allocatedRows[rowId]);
                const bulks = bagValues
                    ? getBagValuesByRowId(AllocationBagType.bulks, bagValues, rowId)
                    : row.getIn(Item.BULKS).toJS();
                const totalsMatch = bagValues
                    ? doesBulksAndAllocationsMatch(
                          allocation as BagValues,
                          bulks as BagValues,
                          rowAllocationData.allocationDimensions
                      )
                    : doesBulksAndAllocationsMatchLegacy(allocation, bulks, rowAllocationData.allocationDimensions);
                if (!totalsMatch) {
                    const isEmptyAllocation = bagValues
                        ? (allocation as BagValues).size === 0
                        : (allocation as IBagTable).r.length === 0;
                    sizingFieldsData[sizeStatusSchema.field_name] =
                        getSizingErrorStatus(sizingFieldsData, isEmptyAllocation, {
                            sizeCurveSchema,
                        }) ?? sizingFieldsData[sizeStatusSchema.field_name];
                }

                updatedRow = bagValues ? updatedRow : updatedRow.setIn(Item.ALLOCATIONS, allocation);
                Object.keys(sizingFieldsData).forEach((fieldToUpdate) => {
                    updatedRow = updatedRow.setIn(
                        ["fields", fieldToUpdate, "string_value"],
                        sizingFieldsData[fieldToUpdate]
                    );
                });

                if (bagValues) {
                    const bagValueMutations = generateAllocationBagMutations(
                        allocatedRows[rowId],
                        rowId,
                        bagValues,
                        AllocationBagType.allocations
                    );

                    const collectionId = rowAllocationData.collectionId;
                    store.dispatch(bulkMutateAllocationBag(collectionId, bagValueMutations));
                }
                store.dispatch(mutateCollectionItem({ itemsId: ITEMS_ID, index: rowIndex, item: updatedRow }));

                assortmentsData.push(sizingFieldsData);
            }
        });

        return assortmentsData;
    });
};

interface IProcessAllocationsByRow {
    allocationResults: any[];
}
const processAllocationsByRow = ({ allocationResults }: IProcessAllocationsByRow) => {
    const assortmentsData = allocationResults.reduce((acc, { rowData }) => {
        if (rowData) {
            acc.push(rowData);
        }
        return acc;
    }, []);

    return Observable.from(assortmentsData);
};

interface IProcessAllocationsBySizePacks {
    allocationResults: any[];
    sizeCurveSchema: ISchemaKey;
    sizeStatusSchema: ISchemaKey;
    selectedSizePacksSchema: ISchemaKey;
    store: any;
    doors: IDoor[];
    ignoreInfeasible?: boolean;
    collectionId: string;
    merchantId: string;
    allocationBag?: AllocationBag;
}

const processAllocationsBySizePacks = ({
    store,
    allocationResults: unfilteredAllocationResults,
    sizeCurveSchema,
    sizeStatusSchema,
    selectedSizePacksSchema,
    doors,
    ignoreInfeasible,
    collectionId,
    merchantId,
    allocationBag,
}: IProcessAllocationsBySizePacks) => {
    const { mutate } = store.getState();
    const collectionItems = mutate.snapshots.get(ITEMS_ID);

    const [mismatchedSizePacksItems, allocationResults] = unfilteredAllocationResults.reduce(
        (acc, result) => {
            const row = result.row;
            if (itemHasPackSizesMismatch(row, result.sizePacks.selectedSizePacks)) {
                return [acc[0].concat([result]), acc[1]];
            }
            return [acc[0], acc[1].concat([result])];
        },
        [[], []]
    );

    const splitRowGroups = splitRowGroupBySizeCurveAndDimension(allocationResults, allocationBag);
    const splitRowGroupRequests = Object.values(splitRowGroups);
    const isLegacyBag = !allocationBag;

    if (!selectedSizePacksSchema) {
        return Observable.from([]);
    }

    if (splitRowGroupRequests.length === 0 && !mismatchedSizePacksItems.length) {
        return Observable.from([]);
    }

    const allocationRequests = splitRowGroupRequests.reduce((acc, splitRowGroup) => {
        if (splitRowGroup.rows.length > 0) {
            const requestPayload = { ...splitRowGroup };

            // `breadcrumbs` is not actual request data
            delete requestPayload.breadcrumbs;

            // Set timeout to 1 hour
            requestPayload.time_limit_ms = 36000000;

            acc.push(
                ajax({
                    url: `${siteConfig.goAPI}/v1/merchants/${merchantId}/collections/${collectionId}/sizing/packs`,
                    method: "POST",
                    body: requestPayload,
                    retry: 3,
                })
                    .map(({ response }) => {
                        return response;
                    })
                    .catch((error) => {
                        return Observable.throw(error);
                    })
            );
        }

        return acc;
    }, []);

    return Observable.concat(
        mismatchedSizePacksItems.length
            ? Observable.from(mismatchedSizePacksItems).flatMap((result: any) => {
                  const { sizingFieldsData = {} } = result;
                  sizingFieldsData[sizeStatusSchema.field_name] = SizingErrors.SelectedSizesAndPacksMismatch;
                  let updatedRow = result.row;
                  Object.keys(sizingFieldsData).forEach((fieldToUpdate) => {
                      updatedRow = updatedRow.setIn(
                          ["fields", fieldToUpdate, "string_value"],
                          sizingFieldsData[fieldToUpdate]
                      );
                  });

                  store.dispatch(mutateCollectionItem({ itemsId: ITEMS_ID, index: result.rowIndex, item: updatedRow }));

                  return [];
              })
            : Observable.from([]),
        Observable.forkJoin(allocationRequests).concatMap((allocationResponses: IAllocationResponseData[]) => {
            const assortmentsData = [];
            const resultsBySizePacks = getSizePacksAllocationsByRow(allocationResponses, splitRowGroups, doors);
            const allocatedRows = resultsBySizePacks.allocationsByRow;

            const infeasibleRows = getInfeasibleRows(allocationResponses, splitRowGroups);
            const mergedRows = Object.keys({ ...allocatedRows, ...infeasibleRows }).reduce((acc, rowId) => {
                acc[rowId] = (allocatedRows[rowId] ?? []).concat(infeasibleRows[rowId] ?? []);
                return acc;
            }, {});

            Object.keys(mergedRows).forEach((rowId) => {
                const rowIndex =
                    collectionItems &&
                    collectionItems.findIndex((collectionItem) => {
                        return collectionItem.getIn(Item.FIELDS.ID) === rowId;
                    });
                const row = collectionItems && collectionItems.get(rowIndex);
                // This is data passed in from earlier concatMap.
                // We shouldn't use `row` from this because it might not have the most recent version.
                const rowAllocationData = allocationResults.find((row) => {
                    return row.row.getIn(Item.FIELDS.ID) === rowId;
                });

                if (row && rowAllocationData && !(ignoreInfeasible && infeasibleRows[rowId])) {
                    let updatedRow = rowAllocationData.row;
                    const sizingFieldsData = { ...rowAllocationData.sizingFieldsData };

                    const allocation = isLegacyBag
                        ? flatToTable(mergedRows[rowId])
                        : flatToBagValues(mergedRows[rowId]);
                    const bulks = isLegacyBag
                        ? row.getIn(Item.BULKS).toJS()
                        : getBagValuesByRowId(AllocationBagType.bulks, allocationBag, rowId);
                    const totalsMatch = isLegacyBag
                        ? doesBulksAndAllocationsMatchLegacy(allocation, bulks, rowAllocationData.allocationDimensions)
                        : doesBulksAndAllocationsMatch(
                              allocation as BagValues,
                              bulks as BagValues,
                              rowAllocationData.allocationDimensions
                          );

                    if (!totalsMatch) {
                        const isEmptyAllocation = isLegacyBag
                            ? (allocation as IBagTable).r.length === 0
                            : isEmptyBagValues(allocation as BagValues);
                        sizingFieldsData[sizeStatusSchema.field_name] =
                            getSizingErrorStatus(
                                {
                                    ...sizingFieldsData,
                                    [selectedSizePacksSchema.field_name]: getSelectedSizePacksForItem(
                                        rowAllocationData.row,
                                        selectedSizePacksSchema
                                    ),
                                },
                                isEmptyAllocation,
                                {
                                    sizeCurveSchema,
                                    selectedSizePacksSchema,
                                }
                            ) ?? sizingFieldsData[sizeStatusSchema.field_name];
                    }

                    if (isLegacyBag) {
                        const packsAllocation = flatToTable(resultsBySizePacks.packAllocationsByRow[rowId] ?? []);
                        updatedRow = updatedRow
                            .setIn(Item.ALLOCATIONS, allocation)
                            .setIn(Item.PACK_ALLOCATIONS, packsAllocation);
                    } else {
                        const allocationBagMutations = generateAllocationBagMutations(
                            resultsBySizePacks.allocationsByRow[rowId] ?? [],
                            row.getIn(Item.FIELDS.ID),
                            allocationBag,
                            AllocationBagType.allocations
                        );

                        const packAllocationBagMutations = generateAllocationBagMutations(
                            resultsBySizePacks.packAllocationsByRow[rowId] ?? [],
                            row.getIn(Item.FIELDS.ID),
                            allocationBag,
                            AllocationBagType.pack_allocations
                        );
                        store.dispatch(
                            bulkMutateAllocationBag(
                                collectionId,
                                allocationBagMutations.concat(packAllocationBagMutations)
                            )
                        );
                    }

                    Object.keys(sizingFieldsData).forEach((fieldToUpdate) => {
                        updatedRow = updatedRow.setIn(
                            ["fields", fieldToUpdate, "string_value"],
                            sizingFieldsData[fieldToUpdate]
                        );
                    });

                    store.dispatch(mutateCollectionItem({ itemsId: ITEMS_ID, index: rowIndex, item: updatedRow }));

                    assortmentsData.push(sizingFieldsData);
                }
            });

            return assortmentsData;
        })
    );
};

export const clearAllocationsEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.ALLOCATIONS.CLEAR)
        .map(({ payload }) => payload)
        .switchMap(({ itemId }) => {
            const { mutate, collection } = store.getState();
            const collectionItems = mutate.snapshots.get(ITEMS_ID);
            if (!collectionItems) {
                return [];
            }
            const allocationBag = getCollectionAllocationBag(store.getState(), collection.collectionId);

            const rowIndex =
                collectionItems &&
                collectionItems.findIndex((collectionItem) => {
                    return collectionItem.getIn(Item.FIELDS.ID) === itemId;
                });
            const row = collectionItems && collectionItems.get(rowIndex);

            if (!row) {
                return [];
            }

            const isLegacyBag = !allocationBag;
            if (!isLegacyBag) {
                return [clearAllocationBag(collection.collectionId, AllocationBagType.allocations, itemId)];
            }

            const updatedRow = row.deleteIn(Item.ALLOCATIONS);
            return [mutateCollectionItem({ itemsId: ITEMS_ID, index: rowIndex, item: updatedRow })];
        });
};

export const copyDoorAllocationEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.ALLOCATIONS.DOOR.COPY)
        .map((action) => action.payload)
        .concatMap(({ collectionId, door }) => {
            const doorAllocation = {};
            const { mutate } = store.getState();
            const collectionObj = mutate.snapshots.get(collectionId);
            const itemsId = collectionObj ? collectionObj.getIn(["items", "ref", "object_id"]) : undefined;
            const collectionItems = mutate.snapshots.get(itemsId);

            collectionItems.forEach((item) => {
                item = item.toJS();

                const id = item.fields.id.string_value;
                const allocationsStr = get(item, "fields._allocations.string_value", "");
                const allocations = parseAllocationsBag(allocationsStr);

                const bulk_total = get(item, "fields." + door + "_total_units.string_value", "");

                const doorData = {
                    bulk: bulk_total,
                    sizes: {},
                };

                if (!allocations) {
                    doorAllocation[id] = doorData;
                    return;
                }

                for (const size in allocations) {
                    if (allocations.hasOwnProperty(size)) {
                        const doorSizeQty = parseInt(allocations[size][door], 10) || 0;

                        doorData.sizes[size] = doorSizeQty;
                    }
                }

                doorAllocation[id] = doorData;
            });

            return [
                {
                    type: COLLECTIONS.COLLECTION.ALLOCATIONS.DOOR.COPY_COMPLETE,
                    payload: { door, doorAllocation },
                },
            ];
        });
};

export const pasteDoorAllocationEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.ALLOCATIONS.DOOR.PASTE)
        .map((action) => action.payload)
        .concatMap(({ itemsId, door, filterAssortmentByBrand }) => {
            const { mutate, collection } = store.getState();
            const pathToAllocations = ["fields", "_allocations", "string_value"];
            const collectionItems = mutate.snapshots.get(itemsId);
            const allocationData = collection.copiedDoorAllocation || {};
            const actions = [];

            collectionItems.forEach((collectionItem, index) => {
                const itemId = collectionItem.getIn(["fields", "id", "string_value"]);
                const brandId = collectionItem.getIn(["fields", "_brand_id", "string_value"]);

                if (filterAssortmentByBrand && filterAssortmentByBrand !== brandId) {
                    return; // filter out
                }

                const doorAllocationData = allocationData[itemId];

                if (!doorAllocationData) {
                    // nothing to paste for this product
                    return;
                }

                const allocationsStr = collectionItem.getIn(pathToAllocations);
                const allocations = parseAllocationsBag(allocationsStr);

                for (const size in allocations) {
                    if (allocations.hasOwnProperty(size)) {
                        const quantity = doorAllocationData.sizes[size] || 0;
                        allocations[size][door] = quantity;
                    }
                }

                const item = collectionItem.toJS();

                item.fields._allocations = { string_value: JSON.stringify(allocations) };
                item.fields[`${door}_total_units`] = { string_value: String(doorAllocationData.bulk) };
                item.fields[`${door}_bulk_units`] = { string_value: String(doorAllocationData.bulk) };

                actions.push(
                    mutateCollectionItem({
                        itemsId,
                        index,
                        item: fromJS(item),
                    })
                );
            });

            return actions;
        });
};

export const bulkMutateAllocationBagEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.BULK_MUTATE)
        .filter(({ payload }) => {
            return payload.values.length > 0;
        })
        .flatMap(({ payload }: { payload: { collectionId: string; values: IMutateAllocationBagValue[] } }) => {
            const { values } = payload;
            const {
                mutate: { snapshots },
            } = store.getState();
            const currentBagValues = snapshots.get(ALLOCATION_BAG_ID);

            if (values.length > MAX_MUTATION_OPERATION_LIMIT) {
                return [];
            }

            const operations = values.map(({ bagType, dimensionValues, quantity, rowId }) => {
                const compoundKey = [rowId, bagType].join(BAG_VALUE_PARAMS_SEPARATOR);

                // If zero quantity and no dimension values specified, we want to clear item's bag
                if (!quantity && dimensionValues.length === 0) {
                    return {
                        type: OperationType.Clear,
                        childKey: compoundKey,
                    };
                }

                const currentValuePath = getAllocationBagValuePath(bagType, rowId, dimensionValues, false);
                let mutatedValue = currentBagValues.getIn(currentValuePath) ?? Map();
                mutatedValue = mutatedValue.set("string_value", (quantity || 0).toString());

                return {
                    type: !quantity ? OperationType.Remove : OperationType.Set,
                    childKey: compoundKey,
                    key: dimensionValues.join(BAG_VALUE_PARAMS_SEPARATOR),
                    value: mutatedValue,
                };
            });

            return [bulkMixedSetOperation(ALLOCATION_BAG_ID, MutableType.Map, operations)];
        });

export const syncBulkWithAllocationEpic: Epic<SyncBulkWithAllocationAction, IStore> = (action$, store) =>
    action$.ofType(COLLECTIONS.COLLECTION.ALLOCATIONS.SYNC_BULK_WITH_ALLOCATION).flatMap(({ payload }) => {
        const actions = [];
        const { collectionId, rowId } = payload;
        const state = store.getState();

        const genericAllocationDimensions = getAllocationDimensionsBySubtype(
            state,
            collectionId,
            AllocationDimensionKind.Generic
        );
        const collectionItems = getCollectionItems(state, collectionId);
        const allocationBag = getCollectionAllocationBag(state, collectionId);
        let syncedAllocationBag;

        const rowIndex = collectionItems.findIndex((item) => item.getIn(Item.FIELDS.ID) === rowId);
        const row = collectionItems.get(rowIndex);

        if (row && amphibiousGetIn(row, Item.FIELDS.SIZE_CURVE_STATUS) === SizingErrors.BulkPackConstraintsMismatch) {
            const rowJs = row.toJS();
            let mutatedItem = row;
            if (!allocationBag) {
                const newBulkTotals = getLegacyBulkTotalsSum(rowJs);
                newBulkTotals && lodashSet(rowJs, [Item.BULKS[0]], newBulkTotals);
                newBulkTotals && (mutatedItem = mutatedItem.setIn(Item.BULKS, get(rowJs, ItemFields.Bulks)));
            } else {
                syncedAllocationBag = syncBulksWithAllocations(allocationBag, rowId);
                const allocationBagMutations = generateReconciliationBagMutations(
                    allocationBag,
                    syncedAllocationBag,
                    rowId,
                    AllocationBagType.bulks
                );
                if (allocationBagMutations.length > 0) {
                    actions.push(bulkMutateAllocationBag(collectionId, allocationBagMutations));
                }
            }
            const stringSelectedsizes = mutatedItem.getIn(Item.FIELDS.SIZES_SELECTED, "");
            const { curveSizes, sizesAddedManually } = parseSelectedSizes(stringSelectedsizes);
            const { sizeCurveStatus } = getUpdatedSizesCurveAndStatus(
                mutatedItem,
                curveSizes,
                sizesAddedManually,
                genericAllocationDimensions,
                syncedAllocationBag
            );
            mutatedItem = mutatedItem.setIn(Item.FIELDS.SIZE_CURVE_STATUS, sizeCurveStatus);

            actions.push(mutateCollectionItem({ itemsId: ITEMS_ID, index: rowIndex, item: mutatedItem }));
        }

        return actions;
    });

export const clearAllocationBagEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.CLEAR)
        .flatMap(({ payload }: { payload: { collectionId: string; bagType: AllocationBagType; itemId: string } }) => {
            const { bagType, itemId } = payload;
            return [bulkClearAllocationBag([itemId], [bagType])];
        });

export const bulkClearAllocationBagEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.BULK_CLEAR)
        .flatMap(({ payload }: { payload: { itemIds: string[]; bagTypes: AllocationBagType[] } }) => {
            const { itemIds, bagTypes } = payload;
            const {
                collection: { collectionId },
                mutate: { snapshots },
            } = store.getState();
            const currentAllocationBag = snapshots.get(ALLOCATION_BAG_ID);
            if (!currentAllocationBag) {
                return [];
            }
            const mutations = [];
            bagTypes.forEach((bagType) => {
                itemIds.forEach((itemId) => {
                    mutations.push({
                        bagType,
                        rowId: itemId,
                        quantity: 0,
                        dimensionValues: [],
                    });
                });
            });

            return [bulkMutateAllocationBag(collectionId, mutations)];
        });

export const replaceBagValuesEpic = (action$, store) =>
    action$.ofType(COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.REPLACE_BAG_VALUES).flatMap(({ payload }) => {
        const { collectionId, bagType, bagValues, rowId } = payload;
        const {
            mutate: { snapshots },
        } = store.getState();
        const currentAllocationBag = snapshots.get(ALLOCATION_BAG_ID);
        const sourceBagValues = getBagValuesByRowId(bagType, currentAllocationBag, rowId);
        const mutations = generateReconciliationMutations(sourceBagValues, bagValues, rowId, bagType);

        return [bulkMutateAllocationBag(collectionId, mutations)];
    });

export const clearAllocationBagMemoizedAndLoading = (action$, store$) =>
    action$
        .ofType(MUTATE_UPDATE_SNAPSHOT_FROM_COMMITTED)
        .filter(({ payload }) => {
            const { committedHEAD, objectId } = payload;
            const { object_type } = committedHEAD;

            const { mutate } = store$.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId);

            return object_type === ALLOCATION_BAG_NAMESPACE && !sentMutation && !uncommittedMutation?.size;
        })
        .switchMap(() => {
            const { collection } = store$.getState();
            sumColumnValues.cache.clear();
            sumRowTotalRetailValues.cache.clear();
            sumRowUnitsValues.cache.clear();
            collection.gridApi?.hideOverlay();

            return Observable.empty();
        });
